import {Location} from '@angular/common';
import {Component, Injector, OnDestroy} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute, Router} from '@angular/router';
import {AvailableLang} from '@discover-swiss/models';
import {TranslateService} from '@ngx-translate/core';
import {Store} from '@ngxs/store';
import {Observable, Subject} from 'rxjs';
import {distinctUntilChanged, filter, map, takeUntil} from 'rxjs/operators';
import {LanguageService} from 'src/app/core/i18n/language.service';
import {MainPageService} from 'src/app/shared/services/main-page.service';
import {GetProfile} from 'src/app/store/profile/profile.actions';
import {IProfileState, ProfileState} from 'src/app/store/profile/profile.state';

@Component({
    standalone: false,
    selector: 'app-base',
    template: '',
})
export class BaseComponent implements OnDestroy {
    public profileState$: Observable<IProfileState>;
    protected activatedRoute: ActivatedRoute;
    protected router: Router;
    protected location: Location;
    protected langService: LanguageService;
    protected translateService: TranslateService;
    protected store: Store;
    protected mainPageService: MainPageService;

    get currentLang(): AvailableLang {
        return this.langService.currentLang;
    }

    protected _destroy$ = new Subject<void>();
    protected readonly destroy$ = this._destroy$.asObservable();

    constructor(protected injector: Injector) {
        this.mainPageService = injector.get(MainPageService);
        this.activatedRoute = injector.get(ActivatedRoute);
        this.router = injector.get(Router);
        this.location = injector.get(Location);
        this.langService = injector.get(LanguageService);
        this.translateService = injector.get(TranslateService);
        this.store = injector.get(Store);

        this.profileState$ = this.store.select((state) => state.profile as IProfileState);
        this.profileState$.pipe(
            filter((profileState) => profileState.hasValue),
            map((profileState) => profileState.data.preferredLanguage),
            takeUntil(this._destroy$),
        ).subscribe((preferredLanguage) => {
            this.langService.setPreferredLang(preferredLanguage as AvailableLang);
        })

        this.activatedRoute.queryParams.pipe(
            map((queryParams) => queryParams.lang),
            filter((lang) => Boolean(lang)),
            takeUntil(this._destroy$),
        ).subscribe({
            next: (lang: AvailableLang) => {
                this.langService.setQueryParamLang(lang);
            },
        });

        this.langService.languageChange$.pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: () => {
                const queryParams = this.activatedRoute.snapshot.queryParams ?? {};
                const availableLangQueryParam = queryParams?.lang;

                if (availableLangQueryParam) {
                    const url = this.router.createUrlTree([], {relativeTo: this.activatedRoute, queryParams: {...queryParams, lang: this.langService.currentLang}}).toString();
                    this.location.replaceState(url);
                }
            }
        });

        const profileHasValue = this.store.selectSnapshot(ProfileState.hasValue);
        const profileLoading = this.store.selectSnapshot(ProfileState.loading);
        if (!profileHasValue && !profileLoading) {
            this.store.dispatch(new GetProfile());
        }

        this.store.select((state) => state.profile as IProfileState).pipe(
            distinctUntilChanged((a, b) => a.error === b.error),
            takeUntil(this._destroy$),
        ).subscribe((profileState) => {
            if (profileState.error) {
                console.error('ProfileState error:', profileState.error);
                this.injector.get(MatSnackBar).open(profileState.error, this.translateService.instant('close'), {
                    panelClass: ['mat-snack-bar', 'mat-snack-bar-error'],
                });
            }
        });
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }
}
